import {
  identityApiRef,
  useApi,
  useApiHolder,
  useRouteRef,
} from '@backstage/core-plugin-api';
import {
  CockpitActivityPage,
  createCockpitNavigationPort,
} from '@internal/spassu-code-cockpit-ui';
import { Button, Chip } from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connectActivityRouteRef } from '../routes';
import { controlPlaneApiRef } from '../api';
import {
  CONNECTION_LABELS,
  EXPIRY_BAND_LABELS,
  WORKSPACE_LABELS,
} from '../components/ControlPlaneManagementPanels';
import { controlPlaneCockpitApiRef } from './ControlPlaneCockpitApi';
import { adaptControlPlaneCockpit } from './controlPlaneCockpitAdapter';

interface CockpitNotice {
  connection: string;
  expiry: string | null;
  workspace: string | null;
}

function useCockpitNotice(activityId: string): CockpitNotice | null {
  const apis = useApiHolder();
  const [notice, setNotice] = useState<CockpitNotice | null>(null);
  useEffect(() => {
    const controlPlaneApi = apis.get(controlPlaneApiRef);
    if (!controlPlaneApi) return undefined;
    const controller = new AbortController();
    let active = true;
    void controlPlaneApi
      .getManagement(activityId, controller.signal)
      .then(response => {
        if (!active) return;
        const projection = response.projection;
        setNotice({
          connection: CONNECTION_LABELS[projection.connection.state],
          expiry:
            projection.connection.expiryBand === 'none'
              ? null
              : EXPIRY_BAND_LABELS[projection.connection.expiryBand],
          workspace: WORKSPACE_LABELS[projection.workspace.state] ?? null,
        });
      })
      .catch(() => undefined);
    return () => {
      active = false;
      controller.abort();
    };
  }, [apis, activityId]);
  return notice;
}

/**
 * Injected through the Cockpit's neutral `headerActions` port. The shared package
 * stays unaware of Control Plane concepts — this component is what knows them, and
 * it lives in the Control Plane plugin (ADR-006, import-boundary test).
 *
 * It reads the canonical projection ONCE, never on an interval. US-003 AC-4 puts
 * the expiry warning on the Cockpit as well as the portfolio, management page and
 * terminal, so navigation alone is not enough — but an earlier version used the
 * polling management hook here, which opened a second poll on every Cockpit visit
 * and crashed the whole page wherever the Control Plane API is not registered.
 * The API is therefore resolved OPTIONALLY and a failed read degrades to the
 * navigation button alone.
 */
const ControlPlaneCockpitHeaderActions = ({
  activityId,
}: {
  activityId: string;
}) => {
  const navigate = useNavigate();
  const connectRoute = useRouteRef(connectActivityRouteRef);
  const notice = useCockpitNotice(activityId);

  const workspacePending =
    notice?.workspace &&
    notice.workspace !== WORKSPACE_LABELS.not_requested &&
    notice.workspace !== WORKSPACE_LABELS.confirmed;

  return (
    <>
      {notice ? (
        <Chip
          size="small"
          role="status"
          label={notice.expiry ?? notice.connection}
          aria-label={`Control Plane: ${notice.connection}${
            notice.expiry ? `; ${notice.expiry}` : ''
          }`}
        />
      ) : null}
      {workspacePending ? (
        <Chip
          size="small"
          role="status"
          label={notice?.workspace}
          aria-label={`Control Plane: ${notice?.workspace}`}
        />
      ) : null}
      <Button
        color="inherit"
        variant="outlined"
        onClick={() => navigate(connectRoute({ activityId }))}
      >
        Gerenciar conexão
      </Button>
    </>
  );
};

type ControlPlaneCockpitPageProps = { activityId: string };

export const ControlPlaneActivityCockpitPage = ({
  activityId,
}: ControlPlaneCockpitPageProps) => {
  const api = useApi(controlPlaneCockpitApiRef);
  const apis = useApiHolder();
  const [metricsPreferenceScope, setMetricsPreferenceScope] = useState<
    string | null
  >(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigation = useMemo(
    () =>
      createCockpitNavigationPort(searchParams, search =>
        setSearchParams(search, { replace: true }),
      ),
    [searchParams, setSearchParams],
  );
  useEffect(() => {
    const identityApi = apis.get(identityApiRef);
    if (!identityApi) return undefined;
    let active = true;
    void identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (active) {
          setMetricsPreferenceScope(
            `${encodeURIComponent(identity.userEntityRef)}:${activityId}`,
          );
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [activityId, apis]);
  return (
    <CockpitActivityPage
      showArtifactLane={false}
      activityId={activityId}
      dataSource={api}
      adaptSnapshot={adaptControlPlaneCockpit}
      navigation={navigation}
      loadingTitle="Carregando cockpit Control Plane"
      metricsPreferenceScope={metricsPreferenceScope}
      headerActions={
        <ControlPlaneCockpitHeaderActions activityId={activityId} />
      }
    />
  );
};
